import React, { useState } from "react";
import styled from "@emotion/styled";
import {
  formatNumber,
  ImpactShopBasketProduct,
  Currency,
} from "@ecologi/shared";
import { Button } from "../../Button";
import { Typography } from "../../Typography";
import {
  FormLayout,
  RowItem,
  RowItems,
} from "../../FormLayout/FormLayout.styles";
import {
  StripeCardNumber,
  StripeCardExpiry,
  StripeCardCVC,
} from "../../StripeElements";
import { Step3Props } from "../types";
import { StepTitle, ButtonContainer } from "./styles";

const PaymentErrorMessage = styled(Typography)`
  color: ${({ theme }) => theme.colors.scarlet};
  margin-top: 16px;
  text-align: center;
`;

const getProductTotal = (
  product: ImpactShopBasketProduct,
  currency: Currency
): number => {
  const price = product.prices.find((p) => p.currency === currency);
  return (price?.amount || 0) * product.quantity;
};

const formatPrice = (amount: number, currency: Currency): string => {
  return formatNumber({ value: amount / 100, currency });
};

export const Step3Payment = ({
  basket,
  currency,
  isSubmitting,
  paymentError,
  onSubmit,
  onBack,
}: Step3Props) => {
  const [isCardNumberValid, setCardNumberIsValid] = useState(false);
  const [isCardCVCValid, setCardCVCIsValid] = useState(false);
  const [isCardExpiryValid, setCardExpiryIsValid] = useState(false);

  const total = basket.reduce(
    (sum, product) => sum + getProductTotal(product, currency),
    0
  );

  const isPaymentValid =
    isCardNumberValid && isCardCVCValid && isCardExpiryValid;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isPaymentValid) {
      onSubmit();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <StepTitle use="dashboardTitle2" as="h2">
        Payment
      </StepTitle>

      <FormLayout>
        <RowItem>
          <StripeCardNumber
            onChange={(e) => setCardNumberIsValid(e.complete)}
            isDisabled={isSubmitting}
            label="Card number"
          />
        </RowItem>
        <RowItems>
          <RowItem>
            <StripeCardExpiry
              onChange={(e) => setCardExpiryIsValid(e.complete)}
              isDisabled={isSubmitting}
              label="Expiry date"
            />
          </RowItem>
          <RowItem>
            <StripeCardCVC
              onChange={(e) => setCardCVCIsValid(e.complete)}
              isDisabled={isSubmitting}
              label="CVC"
            />
          </RowItem>
        </RowItems>
      </FormLayout>

      {paymentError && (
        <PaymentErrorMessage use="body2">{paymentError}</PaymentErrorMessage>
      )}

      <ButtonContainer>
        <Button
          textOnlyWithBorder
          text="Back"
          onClick={(e) => {
            e.preventDefault();
            onBack();
          }}
          disabled={isSubmitting}
          data-testid="checkout-step3-back"
        />
        <Button
          primary
          submit
          text={`Pay ${formatPrice(total, currency)}`}
          disabled={isSubmitting || !isPaymentValid}
          loading={isSubmitting}
          data-testid="checkout-step3-submit"
        />
      </ButtonContainer>
    </form>
  );
};
